"use client";

import { memo, useCallback, useEffect, useId, useState } from "react";
import {
  useA2UIComponent,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";

export const BeeOSDateTimeInput = memo(function BeeOSDateTimeInput({
  node,
  surfaceId,
}: A2UIComponentProps<Types.DateTimeInputNode>) {
  const { resolveString, setValue, getValue } = useA2UIComponent(
    node,
    surfaceId,
  );
  const props = node.properties;
  const id = useId();
  const valuePath = props.value?.path;
  const initialValue = valuePath
    ? (() => { const v = getValue(valuePath); return v != null ? String(v) : ""; })()
    : (resolveString(props.value) ?? "");
  const enableDate = props.enableDate ?? true;
  const enableTime = props.enableTime ?? false;

  const [value, setLocalValue] = useState(initialValue);

  useEffect(() => {
    if (valuePath) {
      const externalValue = getValue(valuePath);
      if (externalValue != null && String(externalValue) !== value) {
        setLocalValue(String(externalValue));
      }
    }
  }, [valuePath, getValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.value?.literalString !== undefined) {
      setLocalValue(props.value.literalString);
    }
  }, [props.value?.literalString]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      if (valuePath) setValue(valuePath, newValue);
    },
    [valuePath, setValue],
  );

  let inputType = "date";
  if (enableDate && enableTime) {
    inputType = "datetime-local";
  } else if (enableTime && !enableDate) {
    inputType = "time";
  }

  const labelText =
    enableDate && enableTime
      ? "Date & Time"
      : enableTime
        ? "Time"
        : "Date";

  const hostStyle: React.CSSProperties =
    node.weight !== undefined
      ? ({ "--weight": node.weight } as React.CSSProperties)
      : {};

  const inputClass =
    "flex h-10 w-full rounded-xl border border-border/40 bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0 focus-visible:border-primary/30 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";

  return (
    <div className="a2ui-datetime-input" style={hostStyle}>
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={id}
          className="text-sm font-medium leading-none text-foreground"
        >
          {labelText}
        </label>
        <input
          type={inputType}
          id={id}
          value={value}
          onChange={handleChange}
          className={inputClass}
        />
      </div>
    </div>
  );
});
